import type {
  BuyerResearchFlagValue,
  BuyerResearchRefinementStatus,
  BuyerResearchRunStatus,
  BuyerResearchRunSummaryDto,
  BuyerResearchSourceOutcome,
} from "@/api/buyerResearch";
import { refineRefusalBlocker } from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import type { StatusTone } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { researchCopy } from "./researchCopy";

/**
 * Pure helpers for the Research tab — no React, no data fetching — so the
 * thresholds, the money rule and the counts sentence are testable without
 * mounting anything.
 */

/**
 * R6 — the fit bands: ≥ 75 `ok` (green), 50–74 `progress` (brass), below 50
 * `muted` (grey). An unscored candidate is muted too.
 *
 * <p>Below 50 is GREY, not red, on purpose. A low fit is a fact about the match,
 * not an exception on the row — red is kept for things that are wrong — and 50
 * is the list's default threshold, so the grey rows are the ones a reader has
 * chosen to see by widening it.</p>
 */
export function scoreTone(score: number | null | undefined): StatusTone {
  if (score == null) return "muted";
  if (score >= 75) return "ok";
  if (score >= 50) return "progress";
  return "muted";
}

export function runStatusTone(status: BuyerResearchRunStatus): StatusTone {
  switch (status) {
    case "Queued":
    case "Running":
      return "progress";
    case "Completed":
      return "ok";
    case "CompletedPartial":
      return "warn";
    case "Failed":
      return "danger";
    case "Cancelled":
      return "muted";
    case "PullQueued":
    case "Pulling":
    case "Refining":
      return "progress";
  }
}

/**
 * Ask Phoenix — the tone for one refinement's own status.
 *
 * <p>An EXHAUSTIVE switch over the union, deliberately, so a member added to
 * the vocabulary fails the build here rather than falling through to a default
 * that paints a new state in whatever colour the last one used.</p>
 *
 * <p>🔴 <b>`refused` is MUTED, not `danger`.</b> Phoenix declining to act on an
 * instruction is an answer, not a fault: nothing broke, nothing was spent, and
 * a red pill on it would teach a specialist that asking is risky. `failed` is
 * the one that really went wrong, and it is the one that gets the red.</p>
 */
export function refinementStatusTone(status: BuyerResearchRefinementStatus): StatusTone {
  switch (status) {
    case "classifying":
    case "confirmed":
    case "applying":
      return "progress";
    case "awaiting confirmation":
      return "warn";
    case "applied":
      return "ok";
    case "failed":
      return "danger";
    case "refused":
    case "cleared":
      return "muted";
  }
}

/**
 * The sentence for an Ask Phoenix blocker code.
 *
 * <p>🔴 <b>ONE MAP SERVES A CLOSED CONTROL AND A REFUSED REQUEST</b>, and one
 * home serves every surface that renders either. The panel says why it will not
 * take a refinement; the panel and the lens over the candidate list both say
 * why one they sent was refused. Two copies of this would be two answers to the
 * same question, and the quiet one would be the one that went stale.</p>
 *
 * <p>A code this client has not learned yet falls back to a readable sentence
 * rather than the slug, and never to nothing — a closed control with no
 * explanation is worse than one whose explanation is general.</p>
 */
export function refinementBlockerSentence(blocker: string | null): string {
  const copy = researchCopy.askPhoenix;
  if (blocker != null && blocker in copy.blocked) {
    return copy.blocked[blocker as keyof typeof copy.blocked];
  }
  return copy.blockedFallback;
}

/**
 * 🔴 A refused refinement is a 400 carrying a blocker CODE in `details.code` —
 * read it with `refineRefusalBlocker`, never from the message, so the surface
 * renders its own copy and a reworded server message cannot silently stop
 * matching.
 *
 * <p>Anything else — a network failure, a 404, a 500 — is not a blocker and
 * falls through to the ordinary message, which is the honest thing to show for
 * a failure that is not a decision.</p>
 */
export function refinementRefusalMessage(e: unknown): string {
  const blocker = refineRefusalBlocker(e);
  return blocker != null ? refinementBlockerSentence(blocker) : extractErrorMessage(e);
}

export function sourceOutcomeTone(outcome: BuyerResearchSourceOutcome): StatusTone {
  switch (outcome) {
    case "succeeded":
      return "ok";
    case "empty":
      return "neutral";
    case "failed":
      return "danger";
    case "skipped":
      return "muted";
  }
}

/**
 * Whether a source's outcome is a STATE ABSENT — the Table and Tile Standard
 * SS6.1 row that renders as muted grey prose rather than as a pill.
 *
 * <p>🔴 <b>`empty` and `skipped` are the common case, not the exception.</b>
 * With the web sources switched off by default, every one of them skips on
 * every single run, and a source that ran and found nothing is an answer rather
 * than a fault. A grey pill on each says only what the word already says, and
 * it says it in the same visual grammar as the red one beside it — which is the
 * inflation SS6.1 names by its consequence: <i>"by the time a real exception
 * appears the reader has stopped seeing pills"</i>.</p>
 *
 * <p>`succeeded` KEEPS its green pill. The module's own precedent is explicit
 * that muting the good outcome <i>"would paint the best result like a dead
 * one"</i> (`buyerListBits.ts`), and SS6's state-present row licenses the green
 * directly. `failed` keeps the red for the same reason in reverse: it is the
 * exception the vocabulary exists to carry.</p>
 *
 * <p>An EXHAUSTIVE switch, like its neighbours, so a fifth outcome is a compile
 * error here rather than falling through to a default that guesses whether a
 * new state is worth interrupting the reader for.</p>
 */
export function sourceOutcomeIsStateAbsent(outcome: BuyerResearchSourceOutcome): boolean {
  switch (outcome) {
    case "empty":
    case "skipped":
      return true;
    case "succeeded":
    case "failed":
      return false;
  }
}

/**
 * 🔴 <b>TWO OF THE FIT CHECKS CAN COST A CANDIDATE ITS RECOMMENDATION, AND THE
 * PAIR IS READ OFF THE SERVER'S OWN GATES RATHER THAN CHOSEN HERE.</b>
 *
 * <p>`BuyerResearchService` computes `Recommended = r.Recommended &&
 * !IndustryContradicted(w) && !FinancialsImplausible(w)`, and those two read
 * exactly these two keys. Everything else in the two groups is INFORMATION: a
 * listing that does not mention seller financing is a fact about the listing,
 * not a fault in it, and drawing it in the same red as a contradicted industry
 * says the opposite.</p>
 *
 * <p>That uniformity is the pill inflation the Table and Tile Standard §6.1
 * names by its consequence — <i>"makes an ordinary book look like a wall of
 * problems, and by the time a real exception appears the reader has stopped
 * seeing pills"</i>. A candidate meeting the program cleanly still fails
 * several checks for want of published figures; under one treatment a reader
 * learns to skip all of them, including the one that means the candidate
 * cannot be recommended at all.</p>
 *
 * <p>⚠️ <b>The keys are string literals on BOTH sides and nothing binds them.</b>
 * The server registers its flag keys as a LIST with no per-key constant, and
 * `IndustryContradicted`'s own doc warns that a rename of one spelling makes
 * its `TryGetValue` miss and the gate stop blocking SILENTLY. The same hazard
 * reaches here: a rename would make this set match nothing and every check
 * would render as information.</p>
 *
 * <p>🔴 <b>What actually covers that, and what does not.</b>
 * `src/test/researchFlagKeyParity.test.ts` reads `BuyerFit` and `ProgramFit`
 * out of the C# source and pins {@link BUYER_FIT_KEYS} and
 * {@link PROGRAM_FIT_KEYS} against them with `toEqual`. Both spellings below
 * are members of those lists, so a rename in C# DOES fail the build — there.
 * <b>Nothing pins this set against the two GATES it mirrors</b>
 * (`IndustryContradicted` / `FinancialsImplausible`): a C# change that moved a
 * gate onto a different key while leaving the registry alone would leave this
 * set matching the wrong thing, silently.</p>
 *
 * <p>An earlier revision of this comment cited `researchFitVerdict.test.ts` as
 * holding both spellings. <b>That file does not exist and never has</b>
 * (`git log --all --diff-filter=A` returns nothing for it) — the citation is
 * recorded here rather than quietly deleted, because a named test is the most
 * effective way to stop the next reader checking whether one is there.</p>
 */
export const DISQUALIFYING_FIT_KEYS: ReadonlySet<string> = new Set([
  "industryMatch",
  "financialsPlausible",
]);

/**
 * How a fit check's verdict is drawn.
 *
 * `plain` — the check passed; it needs no mark, because "fine" is the default
 * and marking it spends the reader's attention on the absence of a problem.
 * `muted` — a fact with no consequence: a "no" on an informational check, or a
 * comparison nobody could make. Prose, per §6's state-absent row.
 * `blocking` — the one case that is an exception in §6.4's sense: this check
 * is why the candidate carries no recommendation.
 */
export type FitVerdictTone = "plain" | "muted" | "blocking";

export interface FitVerdict {
  /** The short word that stands where a pill used to. */
  word: string;
  tone: FitVerdictTone;
  /** Whether a "no" here is what blocks the recommendation. */
  disqualifying: boolean;
}

/**
 * A fit check's label-plus-verdict, replacing the three-colour pill.
 *
 * 🔴 <b>An unknown is NEVER blocking, on either key.</b> Both server gates
 * refuse to act on `unknown` in as many words — a teaser that published no
 * industry is the listing this feature exists to surface — so a client that
 * painted unknown red would be asserting a block the server did not make, on
 * exactly the candidates the feature is for.
 */
export function fitVerdict(key: string, value: BuyerResearchFlagValue | undefined): FitVerdict {
  const words = researchCopy.fitVerdict;
  const disqualifying = DISQUALIFYING_FIT_KEYS.has(key);
  if (value === "yes") return { word: words.yes, tone: "plain", disqualifying };
  if (value === "no") {
    return { word: words.no, tone: disqualifying ? "blocking" : "muted", disqualifying };
  }
  return { word: words.unknown, tone: "muted", disqualifying };
}

/**
 * Whether the run has stopped. Drives the detail poll: a non-terminal run is
 * re-read every {@link RESEARCH_POLL_MS}, a terminal one is left alone.
 *
 * 🔴 <b>An EXHAUSTIVE switch, deliberately, rather than the "not Queued and not
 * Running" test it replaces.</b> That form classified every FUTURE status as
 * terminal by default, which `Pulling` immediately made wrong: a pull whose
 * request died leaves the row `Pulling` until the sweeper releases it, and a
 * client that had already stopped polling would show a spinner for ever. The
 * switch makes the next member a compile error instead of a silent default.
 */
export function isTerminalStatus(status: BuyerResearchRunStatus): boolean {
  switch (status) {
    case "Queued":
    case "Running":
    case "PullQueued":
    case "Pulling":
    case "Refining":
      return false;
    case "Completed":
    case "CompletedPartial":
    case "Failed":
    case "Cancelled":
      return true;
  }
}

/**
 * Whether this run has results of its own on screen.
 *
 * <p>🔴 <b>"STILL WORKING" AND "HAS NOTHING TO SHOW" ARE DIFFERENT QUESTIONS,
 * AND THE SURFACE WAS ANSWERING BOTH WITH {@link isTerminalStatus}.</b> That is
 * right for a FIRST run — a run that is Queued or Running has genuinely
 * produced nothing, and the tab shows a progress list instead. It is wrong for
 * a run that FINISHED and is now doing more work on what it found: `Pulling`
 * buys another page of a list already on screen, and `Refining` puts a lens
 * over one. Under a bare terminal test, submitting a refinement makes the whole
 * candidate list, the brief, the transcript and the pull control vanish until
 * the worker answers — and come back changed, with nothing having said why they
 * went.</p>
 *
 * <p>`Pulling` had the same latent defect and the synchronous pull hid it: the
 * mutation returned before anything re-read the run, so the row was only ever
 * observed `Pulling` when a request died. The pull is QUEUED now (R9), exactly
 * as Ask Phoenix already was, so `PullQueued` and `Pulling` are both genuinely
 * on screen for as long as the work takes — and a list that vanished for that
 * long would be the defect at full size.</p>
 *
 * <p>An EXHAUSTIVE switch for the reason its sibling is one: a member added to
 * the union must be a compile error here rather than falling through to a
 * default that guesses.</p>
 */
export function hasSettledResults(status: BuyerResearchRunStatus): boolean {
  switch (status) {
    case "Queued":
    case "Running":
      return false;
    case "PullQueued":
    case "Pulling":
    case "Refining":
    case "Completed":
    case "CompletedPartial":
    case "Failed":
    case "Cancelled":
      return true;
  }
}

/**
 * RG23 — how far through its current batch the worker is, or null when there is
 * no progress to report.
 *
 * <p>Only a run that is SCORING right now has any: a first run (`Running`), a
 * pull (`Pulling`), or a confirmed Ask Phoenix re-query buying its page
 * (`Refining`), once the rows it bought have been written and the first group
 * of twenty has been asked for. A `Refining` run that buys nothing — a filter
 * or a re-rank — has none: the server clears the counters when the run enters
 * `Refining`, as it does when a pull is queued. Null otherwise, and null too before the
 * server has said how many there are — `scoringPlanned` is null until the
 * scoring pass starts, so a first run still searching or a pull still buying
 * keeps its own sentence rather than reading "0 of 0 done".</p>
 *
 * <p>Null once the count reaches the plan as well: the last group has been
 * saved and the run is writing its batch line, so "the rest will appear" would
 * promise rows there are none of. A batch that bought nothing new plans zero
 * and reports nothing.</p>
 */
export function scoringProgressOf(
  run: Pick<BuyerResearchRunSummaryDto, "status" | "scoringPlanned" | "scoringDone">,
): { done: number; planned: number } | null {
  if (run.status !== "Running" && run.status !== "Pulling" && run.status !== "Refining") return null;
  const planned = run.scoringPlanned ?? 0;
  if (planned <= 0) return null;
  const done = Math.min(Math.max(run.scoringDone ?? 0, 0), planned);
  return done < planned ? { done, planned } : null;
}

/** Estimated spend, always to the cent: `$0.00`, `$3.42`. */
export function formatCost(usd: number | null | undefined): string {
  return formatCurrency(usd ?? 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * R18 — `94 on this run · 6 dismissed · 0 already tied`, plus
 * `· 2 skipped at search (dismissed earlier)` only when that count is above zero.
 *
 * <p>🔴 <b>THE FIGURES ARE NOT ONE KIND OF COUNT, AND THE WORDING SAYS WHICH.</b>
 * "On this run" and "dismissed" are LIVE counts off the list read: every row the
 * run showed, and how many of those the buyer has since dismissed — so the
 * second is a subset of the first. "Already tied" and "skipped at search" are
 * counts the run took once, when it finished: rows the search never showed
 * because the buyer already had them, or had dismissed them on an earlier run.
 * The old header called that last one "dismissed", beside rows a reader could
 * see were dismissed, and the two numbers disagreed.</p>
 */
export function countsHeader(counts: {
  totalOnRun: number;
  dismissedOnRun: number;
  alreadyTiedCount: number;
  skippedAtSearch: number;
}): string {
  const head =
    `${Math.max(0, counts.totalOnRun)} on this run · ` +
    `${Math.max(0, counts.dismissedOnRun)} dismissed · ` +
    `${Math.max(0, counts.alreadyTiedCount)} already tied`;
  return counts.skippedAtSearch > 0
    ? `${head} · ${counts.skippedAtSearch} skipped at search (dismissed earlier)`
    : head;
}

/** `Tempe, AZ` / `Tempe` / `AZ` / null. */
export function locationLine(city: string | null | undefined, state: string | null | undefined): string | null {
  const parts = [city, state].map((p) => p?.trim()).filter((p): p is string => Boolean(p));
  return parts.length ? parts.join(", ") : null;
}

/** `45s` / `2m` / `1h 5m` from an ISO instant to `nowMs`; null when unknown. */
export function elapsedLabel(since: string | null | undefined, nowMs: number): string | null {
  if (!since) return null;
  const startedMs = new Date(since).getTime();
  if (Number.isNaN(startedMs)) return null;
  const seconds = Math.max(0, Math.floor((nowMs - startedMs) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** `1.2s` / `850ms` for a source's duration. */
export function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * 🔴 <b>THE CLIENT DOES NOT SORT CANDIDATES.</b> The list endpoint filters,
 * sorts and pages them (R5), and the client renders the page it is given.
 *
 * <p>Best fit is <i>fit DESC, recommended DESC, batch ASC, title, id</i> (R3);
 * the money sorts put a real figure first, a figure that looks wrong next (a
 * placeholder, or any figure on a row whose figures do not hang together) and
 * a blank last, in both directions. Both are computed in ONE place, the
 * service.
 * `sortCandidates` was deleted from here in #1239 for exactly this reason — two
 * orders in two tiers are free to disagree the next time either moves — and a
 * sort control does not bring it back: it asks the server for a different
 * order rather than computing one.</p>
 */

/**
 * A share on 0..1 as a whole-number percentage.
 *
 * Rounded to whole points on purpose: these are measured off a free count over
 * a population that moves between calls, and a decimal place would claim a
 * precision the measurement does not have.
 */
export function formatShare(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/** The fixed key order of the two flag groups (D4.4), mirrored from
 *  `BuyerResearchFlagKeys`. The server sends exactly these keys. */
export const BUYER_FIT_KEYS: readonly string[] = [
  "industryMatch",
  "inPriceBand",
  "withinGeographyOrRelocatable",
  "cashFlowClearsFloor",
  "revenueClearsFloor",
  "sellerFinancingMentioned",
];

export const PROGRAM_FIT_KEYS: readonly string[] = [
  "askingPriceInProgramBand",
  "cashFlowPresent",
  "sellerFinancingMentioned",
  "impliedMultipleWithinCeiling",
  "financialsPlausible",
];

/**
 * A provenance label → its caption. Labels are `BuyerResearchProvenance`'s
 * shapes: `fromField:X`, `fromField:X, split`, `interview:Q`, or one of the
 * four bare words. An unrecognised label is shown as-is rather than hidden.
 *
 * 🔴 <b>NO LABEL RETURNS `null`, NOT "Not on file".</b> It used to return the
 * sentinel, and the read-only brief rendered a caption under every row
 * unconditionally, so the most repeated string on the research page was a
 * caption saying that a field nobody had filled in had no source. Eight of
 * them, under a panel three times taller than its own content.
 *
 * <p>"We recorded where this came from" and "there is nothing here" are
 * different facts, and only the first is worth a line. The second is carried
 * once for the whole brief by {@link summarizeProvenance} — one note instead of
 * eight repetitions of the same non-event.</p>
 */
export function provenanceCaption(label: string | undefined): string | null {
  const p = researchCopy.drawer.provenance;
  if (!label) return null;
  if (label === "specialist") return p.specialist;
  if (label === "proposedByAi") return p.proposedByAi;
  if (label === "biography") return p.biography;
  if (label === "prequalification") return p.prequalification;
  if (label.startsWith("fromField:")) {
    const rest = label.slice("fromField:".length);
    if (rest.endsWith(", split")) return p.fromFieldSplit(rest.slice(0, -", split".length));
    return p.fromField(rest);
  }
  if (label.startsWith("interview:")) return p.interview(label.slice("interview:".length));
  return label;
}

/**
 * The coarse KIND behind a provenance label — the bucket a summary counts,
 * rather than the caption a row renders.
 *
 * <p>Summarising by caption does not work: `fromField:MaxPurchasePrice` and
 * `fromField:TargetIndustries` are two captions and one source, so a brief
 * drawing six fields off the buyer record would report six "sources" and read
 * as more provenance than it has. The kind is what a reader means by "where did
 * this come from".</p>
 *
 * <p>An unrecognised label buckets as `other` rather than as nothing: it IS a
 * recorded source, and counting it as unattributed would under-report the
 * brief's provenance the first time the server grows a shape this client has
 * not learned.</p>
 */
export type ProvenanceKind =
  | "specialist"
  | "proposedByAi"
  | "biography"
  | "prequalification"
  | "buyerRecord"
  | "interview"
  | "other";

export function provenanceKind(label: string | undefined): ProvenanceKind | null {
  if (!label) return null;
  if (label === "specialist") return "specialist";
  if (label === "proposedByAi") return "proposedByAi";
  if (label === "biography") return "biography";
  if (label === "prequalification") return "prequalification";
  if (label.startsWith("fromField:")) return "buyerRecord";
  if (label.startsWith("interview:")) return "interview";
  return "other";
}

export interface ProvenanceSummary {
  /** Distinct kinds present, in the order they were first seen. */
  kinds: ProvenanceKind[];
  /** How many of the brief's fields carried no recorded source at all. */
  unattributed: number;
}

/**
 * One provenance note for a whole brief, replacing a caption under every row.
 *
 * <p>Order is first-seen rather than alphabetical or by frequency: the rows are
 * rendered in a fixed order a reader is already scanning, so first-seen is the
 * order they will meet the sources in, and any other order asks them to build a
 * second mapping in their head to check the note against the panel.</p>
 */
export function summarizeProvenance(labels: readonly (string | undefined)[]): ProvenanceSummary {
  const kinds: ProvenanceKind[] = [];
  let unattributed = 0;
  for (const label of labels) {
    const kind = provenanceKind(label);
    if (kind == null) {
      unattributed += 1;
      continue;
    }
    if (!kinds.includes(kind)) kinds.push(kind);
  }
  return { kinds, unattributed };
}

/**
 * Parse a whole-dollar text input. Blank → null (unset); digits with optional
 * `$`, commas and a decimal part → the number; anything else → `undefined`
 * (invalid, keep the text and refuse to run).
 */
export function parseMoneyInput(raw: string): number | null | undefined {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/** The inverse: a stored amount as the text a decimal input shows. */
export function moneyInputValue(amount: number | null | undefined): string {
  return amount == null ? "" : String(amount);
}

/**
 * Clef's own tags, out of the JSON array the vendor sent.
 *
 * 🔴 **AN UNPARSEABLE OR NON-ARRAY BLOB RENDERS NOTHING, NEVER THE RAW
 * STRING.** `tagsJson` is stored verbatim as the vendor supplied it, so it is
 * untrusted payload: printing it at a reader on a parse failure shows them a
 * brace-and-quote soup that looks like a defect in Phoenix. Non-string
 * members are dropped for the same reason, blanks are trimmed away, and the
 * order the vendor chose is kept — it is the only ordering anybody has.
 */
export function parseCandidateTags(tagsJson: string | null | undefined): string[] {
  if (!tagsJson) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(tagsJson);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((t): t is string => typeof t === "string")
    .map((t) => t.trim())
    .filter(Boolean);
}

/**
 * Whether a Layer B `status` is worth drawing.
 *
 * 🔴 The mapper DROPS a delisted listing, so every row it keeps reads
 * `active` — a badge saying so on every card is noise a reader learns to stop
 * seeing, which is exactly how a real `delisted` would go unread the day the
 * refresh path starts writing one. Null (every historical row) is likewise
 * nothing to say.
 */
export function isNotableListingStatus(status: string | null | undefined): boolean {
  return Boolean(status) && status!.trim().toLowerCase() !== "active";
}

/**
 * Whether a Layer B `currency` is worth drawing. Same rule, same reason:
 * non-USD rows are dropped at the mapper, so USD is every row it keeps, and
 * the money cells above are stated in it either way.
 */
export function isNotableCurrency(currency: string | null | undefined): boolean {
  return Boolean(currency) && currency!.trim().toUpperCase() !== "USD";
}

/**
 * Whether the count on screen still describes the brief on screen.
 *
 * 🔴 **ONE RULE, TWO READERS** — the conversation strikes the number through
 * and offers a fresh one; the run control refuses to name it on the button.
 * Two copies of a comparison this cheap would be two answers to "is this
 * number about this query?", which is the single question this whole feature
 * exists to make answerable.
 *
 * A count taken against nothing (`countedJson` null) is not stale, it is
 * absent — the caller renders nothing at all for that, never a struck-through
 * figure with no figure behind it.
 */
export function countIsStale(countedJson: string | null, currentJson: string | null): boolean {
  return countedJson != null && currentJson != null && countedJson !== currentJson;
}
